import { ref, computed } from 'vue';

import LogRocket from 'logrocket';

import {
  TOKEN_REFRESH_BUFFER_MS,
  AUTH_REFRESH_LOCK_SESSION,
  AUTH_REQUEST_TIMEOUT_MS,
} from '@/constants/auth';
import { clearSignupAttribution, clearStoredAttribution } from '@/helpers/attribution';
import { oauthQueryRescue } from '@/router/oauthQueryRescue';
import { loginRedirectWithReturn } from '@/router/utils';
import { useLoaderStore } from '@/stores/loader.js';

import * as backendAuth from './backend-auth.service';
import { bpSelection } from './bpSelection';
import { crossTabSync } from './crossTabSync';
import {
  expiresAtFromToken,
  businessProfileIdFromToken,
  scopesFromToken,
} from './jwtExpiry';
import { profileTokens } from './profileTokens';
import { tokenStorage } from './tokenStorage';

const user = ref(tokenStorage.getCachedUser());
const isAuthLoading = ref(false);
const isInitializing = ref(false);
const isLoggingOut = ref(false);
const isAuthenticated = computed(() => !!user.value);

// The backend-issued pre-profile identity token (access + refresh). It authorizes the
// manager auth endpoints (/users/me, /auth/switch-profile, /business-profiles/*) and is the
// proof of session used to mint per-BP app tokens. Persisted in wp.auth.session so a reload
// can restore the session without Cognito.
const sessionToken = ref(tokenStorage.loadSession());

// Whether the user signed in via a federated provider (Google/Amazon/…) rather than email+password.
// Derived from the backend-provided provider claim (COGNITO = email/password form signup).
const isSocialLogin = computed(() => {
  const provider = user.value?.provider;
  return !!provider && provider !== 'COGNITO';
});

let routerRef = null;
const signOutListeners = [];
export const onSignOut = (fn) => {
  signOutListeners.push(fn);
  return () => {
    const i = signOutListeners.indexOf(fn);
    if (i !== -1) signOutListeners.splice(i, 1);
  };
};

// Run every sign-out listener (CASL reset + account-store clear, incl. the persisted
// currentBusinessProfile/bpSelection). Shared by logout and the restore-rejection cleanup.
function notifySignOut() {
  signOutListeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.error('signOut listener error:', e);
    }
  });
}

export function setRouter(router) {
  routerRef = router;
}

// A token-issuing response without a refreshToken silently strips the stored pair and the next
// refresh throws 'No session refresh token' — warn at write time so LogRocket/Sentry name the endpoint.
function warnIfNoRefreshToken(source, data) {
  if (data?.refreshToken) return;
  console.warn(`[auth] token payload from ${source} has no refreshToken`, {
    hasAccessToken: !!data?.accessToken,
  });
}

// Persist the identity session token (access + refresh) from a token-only login/exchange response.
// Profile, scopes and the active business profile are no longer in the response body — they are
// pulled from /users/me and applied by seedActiveProfile().
function setSession(data, { isSignup = false } = {}) {
  warnIfNoRefreshToken('login/confirm/mfa/recovery/federated', data);
  const entry = {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: expiresAtFromToken(data.accessToken),
  };
  sessionToken.value = entry;
  tokenStorage.persistSession(entry);
  // Session established (password / MFA / recovery / federated / confirm) — the attribution
  // bundle either already rode a user-creating request (register/confirm/oauth exchange) or
  // belongs to an existing account; drop it so it can't attach to someone else's future signup.
  clearStoredAttribution({ preserveForSignup: isSignup });
  // A fresh session supersedes any OAuth state captured for a previous one — drop stale
  // sessionId/code keys so the connect flow can't poll a prior session's id (403 loop).
  // setSession is only called on genuine login/exchange transitions (not refresh/switch),
  // so this never wipes mid-flow state. A Shopify claim handoff is kept: it came from Shopify
  // admin and is exactly what the user signed in to finish.
  oauthQueryRescue.clearCaptured({ keepHandoff: true });
}

// Record the active-profile selection right after a fresh login/exchange, using the /users/me
// profile. The identity token is already scoped to the user's default business profile and serves
// it directly (routed by tokenResolver) — nothing is copied into a wp.auth.bp.<default> store,
// which would create a second owner of the same refresh chain (the shared-sid logout).
function seedActiveProfile(profile) {
  const bpId = profile?.businessProfileId;
  if (bpId != null) bpSelection.set(bpId);
}

// Decoded-claims cache keyed by the raw access-token string. getSessionBpId/getSessionScopes sit
// on hot paths (every business-API request via tokenResolver, the CASL watcher) and a given
// token's claims never change — decode once per rotation instead of per call. The stable scopes
// array identity also spares the deep CASL watcher a fresh array every evaluation.
let claimsCache = { token: null, bpId: null, scopes: [] };
function cachedClaims() {
  const token = sessionToken.value?.accessToken ?? null;
  if (claimsCache.token !== token) {
    claimsCache = {
      token,
      bpId: businessProfileIdFromToken(token),
      scopes: scopesFromToken(token),
    };
  }
  return claimsCache;
}

// The default business profile served by the session token (its `businessProfileId` claim), as a
// string, or null. Stable across /auth/refresh (refresh re-scopes only the scope set, not the BP).
function getSessionBpId() {
  return cachedClaims().bpId;
}

// Synchronous raw reads of the session token — for the CASL/computed snapshot resolver, which
// cannot await a refresh. They never trigger network I/O.
function getSessionAccessToken() {
  return sessionToken.value?.accessToken ?? null;
}
function getSessionScopes() {
  return cachedClaims().scopes;
}

// Async token for a business-API request scoped to the default profile: ensure the session token
// is fresh (rotating it if needed), then return it. Used by tokenService for the default BP, which
// has no per-BP app token of its own.
async function getValidSessionToken() {
  if (!sessionToken.value?.accessToken) return null;
  if (isSessionExpired()) await refreshSession();
  return sessionToken.value?.accessToken ?? null;
}

// Federated login completes via the backend handoff exchange — no password, no Remember-me
// checkbox. Always persist (localStorage) so the Google/Amazon session survives a browser restart,
// regardless of any prior form-login Remember-me choice left on the storage backend in this tab.
export function setFederatedSession(data, { isSignup = false } = {}) {
  tokenStorage.setRemember(true);
  setSession(data, { isSignup });
}

function clearSession() {
  clearSignupAttribution();
  sessionToken.value = null;
  tokenStorage.clearSession();
}

export function hasSession() {
  return !!sessionToken.value?.accessToken;
}

function isSessionExpired() {
  const t = sessionToken.value;
  if (!t?.accessToken || !t?.expiresAt) return true;
  return Date.now() > t.expiresAt - TOKEN_REFRESH_BUFFER_MS;
}

// Same-tab contract shared with profileTokens.update(): notifications.js reconnects the
// WebSocket on this window event. The default profile is served by the session token, so its
// rotation must announce too — otherwise the WS keeps the pre-rotation JWT until the socket
// drops and only heals lazily via its token provider.
function announceSessionTokensUpdated(entry) {
  const businessProfileId = businessProfileIdFromToken(entry.accessToken);
  if (businessProfileId == null) return; // pre-profile identity token — nothing listens for it
  window.dispatchEvent(
    new CustomEvent('tokensUpdated', {
      detail: {
        businessProfileId,
        tokens: {
          access: entry.accessToken,
          refresh: entry.refreshToken,
          scopes: scopesFromToken(entry.accessToken),
        },
      },
    }),
  );
}

// Rotate the session token via /auth/refresh. Assumes the caller already holds
// AUTH_REFRESH_LOCK_SESSION (so it must never re-request it). `/auth/refresh` is a public endpoint
// (no session token attached — see PUBLIC_AUTH_URLS), so this POST does not re-enter the lock.
// `rejectedToken` (with `force`): the access token the backend just 401'd. When shared storage
// already holds a different (newer) one, another tab or an earlier refresh rotated the session
// after that request was built, so the stored token is adopted and no network rotation happens.
async function refreshSessionUnlocked(force = false, rejectedToken = null) {
  const stored = tokenStorage.loadSession(); // pick up another tab's rotation
  if (stored) sessionToken.value = stored;
  if (force && rejectedToken && stored?.accessToken && stored.accessToken !== rejectedToken) return;
  if (!force && !isSessionExpired()) return;
  const refreshToken = sessionToken.value?.refreshToken;
  if (!refreshToken) {
    // Session-expiry / logout transition (a cross-tab clear can wipe the pair mid-request) —
    // the forced-logout / redirect path handles the UX. Tag it like tokenService's mint path
    // so the alerts store silences the toast instead of surfacing raw noise.
    const error = new Error('No session refresh token');
    error.code = 'ERR_NO_SESSION';
    throw error;
  }
  const data = await backendAuth.refreshSession(refreshToken);
  warnIfNoRefreshToken('/auth/refresh', data);
  const entry = {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: expiresAtFromToken(data.accessToken),
  };
  sessionToken.value = entry;
  tokenStorage.persistSession(entry);
  announceSessionTokensUpdated(entry);
}

// Rotate the session token via /auth/refresh. `force` bypasses the time-based expiry check
// (used by the 401 handler, where the token was rejected even though it may not be time-expired).
export async function refreshSession(force = false, { rejectedToken = null } = {}) {
  return crossTabSync.withRefreshLock(AUTH_REFRESH_LOCK_SESSION, () =>
    refreshSessionUnlocked(force, rejectedToken),
  );
}

// POST /auth/switch-profile while the caller holds AUTH_REFRESH_LOCK_SESSION. The request is
// __noSessionRefresh, so the axios 401 handler cannot recover it; on a 401 do exactly ONE
// recovery here (adopt a newer token from shared storage, else force one rotation) and retry
// once. A second 401 propagates unchanged.
async function switchProfileUnderSessionLock(bpId) {
  const send = () =>
    backendAuth.switchProfile(bpId, { recordLastUsed: true, sessionLockHeld: true });
  const sentToken = sessionToken.value?.accessToken ?? null;
  try {
    return await send();
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    await refreshSessionUnlocked(true, sentToken);
    return send();
  }
}

// Explicit switch BACK to the default (session) profile. The default profile is served by the
// session token, so switching to it rotates the session's OWN refresh chain and the result must
// replace wp.auth.session (never a wp.auth.bp.<default> store) so there stays a single owner of
// that sid. Records the selection as last-used (an explicit user switch).
export async function switchToDefaultProfile(bpId) {
  return crossTabSync.withRefreshLock(AUTH_REFRESH_LOCK_SESSION, async () => {
    if (crossTabSync.signOutInProgress) return null;
    // Ensure a fresh session token INSIDE the lock via the unlocked core: the switch-profile POST
    // goes through the manager client, whose interceptor calls getSessionToken → refreshSession,
    // which would re-request this same non-reentrant lock and deadlock. Doing the refresh here
    // (we already hold the lock) both avoids that re-entry and closes the window where the token
    // could expire while we waited to acquire the lock.
    // Always go through the unlocked core: it reloads shared storage first (another tab, or an
    // earlier refresh, may have rotated the session while we waited for the lock) and rotates only
    // when the token is really expired. Gating on the cached token's expiry sent an unexpired but
    // already-rotated (sid no longer ACTIVE) token and got a 401 right after signup.
    await refreshSessionUnlocked();
    // sessionLockHeld → __noSessionRefresh: the interceptor must not refresh for this request and
    // the 401 handler must not invalidateAndRefresh (→ refreshSession → this same lock) — a 401
    // here is surfaced to the caller instead of deadlocking the origin.
    const data = await switchProfileUnderSessionLock(bpId);
    warnIfNoRefreshToken('/auth/switch-profile', data);
    const entry = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: expiresAtFromToken(data.accessToken),
    };
    sessionToken.value = entry;
    tokenStorage.persistSession(entry);
    announceSessionTokensUpdated(entry);
    // Drop any stale duplicate an older build may have left for the default BP — from memory AND
    // storage (a storage-only removal would leave the hydrated tokensMap entry serving/refreshing
    // the stale chain until the next reload).
    profileTokens.remove(bpId);
    return entry;
  });
}

// Onboarding adoption of the FIRST business profile: the session token issued by /auth/confirm
// is pre-profile (no businessProfileId claim), so tokenResolver would route the just-created
// default profile to the per-BP path and mint a wp.auth.bp.<default> duplicate of the session's
// refresh chain (the WP-981 spontaneous-logout relapse). Re-scope the session to the new profile
// via switch-profile so the session token stays the single owner of its chain. No-op when the
// session is already scoped to a profile — creating an additional profile must never silently
// re-scope the session (explicit switching owns that).
let claimInFlight = null;
// Failure latch: after a claim fails (already past its one built-in refresh-and-retry), every
// business request would re-send the same doomed switch-profile through the resolver gate. Remember
// the failure and re-throw it, without a request, until the session token changes or the cooldown
// passes. An explicit retry ({ force: true }) bypasses and clears it.
const CLAIM_FAILURE_COOLDOWN_MS = 30_000;
let claimFailure = null;

function latchedClaimFailure(bpId) {
  if (!claimFailure) return null;
  const sameClaim =
    claimFailure.bpId === String(bpId) &&
    claimFailure.token === (sessionToken.value?.accessToken ?? null) &&
    Date.now() - claimFailure.at < CLAIM_FAILURE_COOLDOWN_MS;
  if (!sameClaim) claimFailure = null;
  return claimFailure;
}

export async function claimDefaultProfile(bpId, { force = false } = {}) {
  if (bpId == null || !hasSession()) return null;
  if (getSessionBpId() != null) return null;
  // Concurrent pre-profile callers (the first ads request via the resolver gate, the auth-store
  // watcher, the restore heal) must collapse to a SINGLE switch-profile — a second re-scope would
  // rotate the same chain again for nothing. Mirrors restoreInFlight.
  if (claimInFlight) return claimInFlight;
  if (force) claimFailure = null;
  else {
    const latched = latchedClaimFailure(bpId);
    if (latched) throw latched.error;
  }
  claimInFlight = switchToDefaultProfile(bpId)
    .then((entry) => {
      claimFailure = null;
      return entry;
    })
    .catch((error) => {
      // Keyed on the token the NEXT attempt would send: the in-lock refresh may have rotated it.
      claimFailure = {
        bpId: String(bpId),
        token: sessionToken.value?.accessToken ?? null,
        at: Date.now(),
        error,
      };
      throw error;
    })
    .finally(() => {
      claimInFlight = null;
    });
  return claimInFlight;
}

// Resolve the token for manager auth requests (/users/me, /user-settings, /business-profiles/*, …).
//
// Prefer the current business profile's scoped app token: it carries businessProfileId + scopes,
// so profile-scoped manager endpoints work and follow the active profile (re-minted on every
// switch). Fall back to the identity session token for pre-profile requests — before a profile is
// selected, or to authorize /auth/switch-profile itself (which only needs the userId claim).
export async function getSessionToken({ requestUrl, skipRefresh = false } = {}) {
  // During logout, skip every refresh path: the forced logout can originate from inside
  // refreshSession's Web Lock (the /auth/refresh 401 handler in $axios.js), and re-requesting
  // the same non-reentrant lock here would deadlock all session-token requests origin-wide.
  // A stale token is acceptable — /auth/logout failures are caught and local sign-out proceeds.
  if (isLoggingOut.value) return sessionToken.value?.accessToken ?? null;
  // The request is fired while its caller holds AUTH_REFRESH_LOCK_SESSION (__noSessionRefresh,
  // switch-to-default) — a refresh here would re-request that non-reentrant lock and deadlock.
  // The caller refreshed the session inside the lock right before firing, so the in-memory
  // token is the freshest available.
  if (skipRefresh) return sessionToken.value?.accessToken ?? null;
  // /auth/switch-profile authorizes with the identity token alone (userId claim) AND runs from
  // inside the per-BP refresh lock (fetchInitial / switchTo hold wp-auth-app-refresh-<bp> while
  // the request fires). Entering the per-BP branch below would re-request that same
  // non-reentrant lock from the same tab and deadlock before any network I/O — the exact
  // "stale BP token + cached user" eternal-preloader scenario. Go straight to the session token.
  const selected = requestUrl === '/auth/switch-profile' ? null : bpSelection.get();
  // The DEFAULT profile is served by the session token itself (it carries businessProfileId +
  // scopes), so the per-BP branch must be skipped for it: a wp.auth.bp.<default> entry can only
  // be a stale duplicate of the session's refresh chain (left by an old build or written by an
  // old-build tab and synced cross-tab) — using it, and especially refreshing it, would rotate a
  // reuse-flagged chain and could revoke the whole session. Skipping makes any leftover inert.
  const bpId = selected != null && selected === getSessionBpId() ? null : selected;
  if (bpId) {
    // If the current profile's app token is stale but refreshable, rotate it so profile-scoped
    // manager requests (e.g. /user-settings) keep a token carrying this businessProfileId + scopes
    // instead of falling back to the identity token (which may be scoped to a different profile).
    // Refresh only — never mint via switch-profile here, since that endpoint authorizes through
    // this very function and would recurse/deadlock.
    if (profileTokens.isExpired(bpId) && profileTokens.hasRefreshToken(bpId)) {
      try {
        await profileTokens.refresh(bpId);
      } catch {
        /* fall through to the identity session token */
      }
    }
    const appToken = profileTokens.getFor(bpId);
    if (appToken && !profileTokens.isExpired(bpId)) return appToken;
  }
  if (!sessionToken.value?.accessToken) return null;
  if (isSessionExpired()) await refreshSession();
  return sessionToken.value?.accessToken ?? null;
}

let restoreInFlight = null;
export function restore() {
  if (restoreInFlight) return restoreInFlight;
  restoreInFlight = doRestore().finally(() => {
    restoreInFlight = null;
  });
  return restoreInFlight;
}

async function doRestore() {
  isInitializing.value = true;
  try {
    const stored = tokenStorage.loadSession();
    if (!stored?.accessToken) {
      user.value = null;
      tokenStorage.clearCachedUser();
      return false;
    }
    sessionToken.value = stored;
    const profile = await backendAuth.me({ timeout: AUTH_REQUEST_TIMEOUT_MS });
    user.value = { ...profile };
    tokenStorage.cacheUser(user.value);
    identifyLogRocket();
    // Heal a pre-profile session on reload (a registration/OAuth token that was never re-scoped):
    // adopt the user's default profile into the SESSION token BEFORE the auth-store watcher or
    // syncBusinessProfiles run, so no wp.auth.bp.<default> duplicate is minted (WP-981). Guarded +
    // best-effort: no-op when the user has no profile yet or the session is already scoped, and a
    // failure must not break restore (the resolver gate is the lazy backstop).
    if (getSessionBpId() == null && profile?.businessProfileId != null) {
      try {
        await claimDefaultProfile(profile.businessProfileId);
      } catch (e) {
        console.warn('[auth] restore-time default-profile re-scope failed:', e?.message);
      }
    }
    return true;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      // Token genuinely rejected — treat as logged out (silent, no toast). Full cleanup that
      // matches logout: identity session AND per-BP tokens (storage + in-memory tokensMap),
      // otherwise stale wp.auth.bp.* tokens survive and a later business request would send a
      // token the backend already rejected.
      console.warn('[auth] session restore rejected (auth):', error?.message);
      user.value = null;
      clearSession();
      tokenStorage.clearCachedUser();
      profileTokens.clearAll();
      // Match logout's side-effect cleanup too: without it a stale currentBusinessProfile /
      // bpSelection / captured oauth_session_id survives on a Public page (the guard only heals
      // needToAuthorize routes) and leaks into the NEXT account in this tab — the resolver gate
      // would then hammer /auth/switch-profile with a foreign BP and the OAuth poll would 403.
      // Best-effort: during earliest bootstrap the auth store (and its listener) may not exist yet.
      notifySignOut();
      // Keeps a Shopify claim handoff: a merchant arriving from Shopify admin with an expired
      // session signs in next and claims it.
      oauthQueryRescue.clearCaptured({ keepHandoff: true });
      return false;
    }
    // Transient failure (network / 5xx / timeout) — do NOT wipe tokens or sign the user out;
    // the cached user (if any) keeps the app usable and the next request / reload can recover.
    console.warn('[auth] session restore failed (transient), keeping session:', error?.message);
    return false;
  } finally {
    isInitializing.value = false;
  }
}

function identifyLogRocket() {
  try {
    if (!isAuthenticated.value) return;
    const u = user.value || {};
    LogRocket.identify(String(u.userId ?? u.sub ?? 'unknown'), {
      email: u.email || 'unknown',
      businessProfileId: u.businessProfileId ?? null,
    });
  } catch (error) {
    console.error('LogRocket identify error:', error);
  }
}

// login.vue inspects the multi-outcome response/error itself (409 federated, 403 unconfirmed,
// 401 bad credentials), so this delegate does not toast — it just stores the session on success.
export async function login({ email, password, rememberMe = true }) {
  isAuthLoading.value = true;
  try {
    // Choose the token storage backend BEFORE writing any token: localStorage when "Remember me"
    // is on (persists across browser restart), sessionStorage when off (cleared on browser close).
    tokenStorage.setRemember(rememberMe);
    const data = await backendAuth.login({ email, password });
    setSession(data);
    crossTabSync.announceSignIn();
    // The token-only login response carries no profile; pull /users/me for the user, active
    // business profile and scopes, then seed the active profile's app token. Best-effort: the
    // session is already established, so a transient failure must not break login (the per-BP
    // token is then minted lazily via /auth/switch-profile on the first scoped request).
    try {
      const profile = await refreshUser();
      seedActiveProfile(profile);
    } catch {
      // ignored — session is set; profile + per-BP token will fill in on next fetch
    }
    return data;
  } finally {
    isAuthLoading.value = false;
  }
}

// Completes a login that required MFA: the SPA collected the code for the challenge
// returned by /auth/login (401 mfa_required) and exchanges it for a session here.
export async function verifyMfa({ email, session, challengeName, code, rememberMe = true }) {
  isAuthLoading.value = true;
  try {
    tokenStorage.setRemember(rememberMe);
    const data = await backendAuth.verifyMfa({ email, session, challengeName, code });
    setSession(data);
    crossTabSync.announceSignIn();
    try {
      const profile = await refreshUser();
      seedActiveProfile(profile);
    } catch {
      // ignored — session is set; profile + per-BP token will fill in on next fetch
    }
    return data;
  } finally {
    isAuthLoading.value = false;
  }
}

// Completes a login that required MFA when the user can't reach their device: a recovery
// code (with the password) is exchanged for a session. Same session setup as verifyMfa.
export async function recoveryVerify({ email, password, code, rememberMe = true }) {
  isAuthLoading.value = true;
  try {
    tokenStorage.setRemember(rememberMe);
    const data = await backendAuth.mfaRecoveryVerify({ email, password, code });
    setSession(data);
    crossTabSync.announceSignIn();
    try {
      const profile = await refreshUser();
      seedActiveProfile(profile);
    } catch {
      // ignored — session is set; profile + per-BP token will fill in on next fetch
    }
    return data;
  } finally {
    isAuthLoading.value = false;
  }
}

// Where logout's in-SPA redirect goes, or null to stay put. A sign-out this tab's user chose lands
// on a plain login. One they didn't — a forced logout (expired session / rejected refresh) or a
// sign-out broadcast from another tab — keeps the protected page they were on as `?to=`, so
// signing back in returns them there instead of to the dashboard.
function signOutRedirectTarget(source) {
  if (source === 'user') return { name: 'login' };
  let current = routerRef.currentRoute.value;
  // Nothing has settled yet: the initial navigation is still pending (a forced logout from inside
  // session restore on reload). Its target is the address-bar URL — the page to come back to.
  if (current.matched.length === 0) {
    const { pathname, search, hash } = window.location;
    current = routerRef.resolve(`${pathname}${search}${hash}`);
  }
  // Already on login: navigating again would only drop an existing `?to=`.
  if (current.name === 'login') return null;
  return current.meta?.needToAuthorize ? loginRedirectWithReturn(current) : { name: 'login' };
}

// source: 'user' | 'forced' | 'broadcast'. 'user'/'forced' notify the backend; 'broadcast' is a
// signOut received from another tab — don't re-announce or it storms across tabs.
export async function logout({ source = 'user', redirect = true } = {}) {
  if (isLoggingOut.value) return;
  isLoggingOut.value = true;
  crossTabSync.setSignOutInProgress(true);
  isAuthLoading.value = true;
  // Raise the same full-screen preloader the login flows use (login.vue / oauth-landing.vue),
  // so the awaited /auth/logout round-trip and the redirect are not a blank, feedback-less pause.
  // Only for a redirecting logout — the silent clearTokens({ redirect: false }) path must not
  // touch the overlay. hardRedirect stays true on the full-page Cognito nav, which tears the
  // overlay down itself; the in-SPA router push has nothing else to drop it, so hide it below.
  const loaderStore = useLoaderStore();
  if (redirect) loaderStore.showLoader();
  let hardRedirect = false;
  try {
    // Clear local state FIRST, then notify the backend best-effort with the token captured
    // before the wipe. The reverse order let /auth/logout's interceptor token resolution
    // re-enter the session-refresh lock mid-refresh and deadlock the whole origin (see the
    // isLoggingOut guard in getSessionToken).
    const accessToken = sessionToken.value?.accessToken ?? null;
    const refreshToken = sessionToken.value?.refreshToken ?? null;
    user.value = null;
    clearSession();
    tokenStorage.clearAll();
    profileTokens.clearAll();
    // Drop any OAuth state captured in this tab so it can't leak into the next session.
    oauthQueryRescue.clearCaptured();
    let logoutUrl = null;
    // 'invite-mismatch' is a deliberate sign-out (the user chose to switch accounts), so the
    // backend session is revoked like a manual one; it just skips the Cognito hard redirect.
    if (source === 'user' || source === 'forced' || source === 'invite-mismatch') {
      try {
        const res = await backendAuth.logout(accessToken, refreshToken);
        logoutUrl = res?.logoutUrl ?? null;
      } catch (e) {
        console.error('logout error:', e);
      }
    }
    if (source !== 'broadcast') crossTabSync.announceSignOut();
    // A user-initiated logout hard-navigates through the Cognito /logout URL so the managed-login
    // session cookie is cleared — otherwise the next federated authorize reuses it and Google never
    // shows the account chooser. Forced (401/refresh-failure) and cross-tab 'broadcast' logouts keep
    // the lightweight in-app push: a forced logout can run from inside session restore (the
    // /auth/refresh 401 handler during bootstrap) where the login navigation waits on
    // bootstrap.waitForReady() → restore(), and a full-page redirect there would fight that chain.
    if (redirect) {
      if (source === 'user' && logoutUrl) {
        hardRedirect = true;
        window.location.href = logoutUrl;
      } else if (routerRef) {
        // Read before the push: a pending initial navigation leaves currentRoute unmatched.
        const initialNavigationPending = routerRef.currentRoute.value.matched.length === 0;
        const target = signOutRedirectTarget(source);
        const navigation = target ? routerRef.push(target).catch(() => {}) : null;
        // Keep the overlay up until login has rendered — dropping it first shows the signed-out
        // page for as long as the login chunk takes to load. Never while the initial navigation is
        // pending: that push waits on bootstrap → restore → this logout, so awaiting would deadlock.
        if (navigation && !initialNavigationPending) await navigation;
      }
    }
  } finally {
    notifySignOut();
    isLoggingOut.value = false;
    isAuthLoading.value = false;
    crossTabSync.setSignOutInProgress(false);
    // Keep the overlay up through the full-page Cognito redirect (navigation ends it); otherwise
    // drop it so the in-SPA login route isn't left behind the preloader. The redirect guard means
    // we only ever hide a loader this call itself raised.
    if (redirect && !hardRedirect) loaderStore.hideLoader();
  }
}

// Reload the current user from /users/me (e.g. after linking/unlinking a provider).
export async function refreshUser() {
  const data = await backendAuth.me();
  if (data) {
    user.value = { ...user.value, ...data };
    tokenStorage.cacheUser(user.value);
    // Identify here so every path that establishes a user — login, verifyMfa, recoveryVerify,
    // confirmRegistration, OAuth landing — tags the LogRocket session. Previously only session
    // restore identified, leaving fresh in-tab logins anonymous until the next reload.
    identifyLogRocket();
  }
  return data;
}

// Drop an invite from the in-memory user (and its cache) once it was accepted or rejected, so the
// invite banner updates without another /users/me round trip. No network, cannot fail.
export function removeInvite(code) {
  const current = user.value;
  if (!current || !Array.isArray(current.invites)) return;
  user.value = { ...current, invites: current.invites.filter((invite) => invite.code !== code) };
  tokenStorage.cacheUser(user.value);
}

// Update the current user's profile via PATCH /users/me and refresh the in-memory user.
export async function updateProfile(payload) {
  const data = await backendAuth.updateProfile(payload);
  if (data) {
    user.value = { ...user.value, ...data };
    tokenStorage.cacheUser(user.value);
  }
  return data;
}

export const register = (payload) => backendAuth.register(payload);

// Confirm the sign-up code and establish the session from the returned token pair.
// /auth/confirm now auto-signs-in (a correct code proves email ownership), so there is no
// separate login call and no need to keep the password around — the old flow lost the
// auto-login if the page reloaded between confirm and login.
export async function confirmRegistration({ email, code, rememberMe = true, isSignup = false }) {
  isAuthLoading.value = true;
  try {
    tokenStorage.setRemember(rememberMe);
    const data = await backendAuth.confirm({ email, code });
    // Only the registration step opts in; confirming an unverified account from login
    // must not carry this browser's acquisition source into a later business profile.
    setSession(data, { isSignup });
    crossTabSync.announceSignIn();
    try {
      const profile = await refreshUser();
      seedActiveProfile(profile);
    } catch {
      // ignored — session is set; profile + per-BP token will fill in on next fetch
    }
    return data;
  } finally {
    isAuthLoading.value = false;
  }
}

export const resendSignUpCode = (email) => backendAuth.resendCode(email);

export const resetPassword = (email) => backendAuth.forgotPassword(email);

export const confirmPasswordReset = ({ email, code, newPassword }) =>
  backendAuth.resetPassword({ email, code, newPassword });

export const updatePassword = ({ oldPassword, newPassword }) =>
  backendAuth.changePassword({ oldPassword, newPassword });

// Federated-only account: set a first password, then refresh the user so the new
// COGNITO method (and unlocked change-password form) is reflected immediately.
export const setPassword = async ({ newPassword }) => {
  await backendAuth.setPassword({ newPassword });
  await refreshUser();
};

export function handleLoginWithAmazonAds({ authorizeUrl, sessionId }) {
  if (sessionId) sessionStorage.setItem('amazon_ads_oauth_session_id', sessionId);
  window.location.href = authorizeUrl;
}

export function handleLoginWithAmazonSellingPartner(domain, redirectUrlPath) {
  const applicationId =
    import.meta.env.VITE_APP_AMAZON_SP_APPLICATION_ID ||
    'amzn1.sp.solution.4340c094-de22-4dda-91ec-6a7634f5508c';
  const state = 'stateexample';
  const redirectUri = redirectUrlPath
    ? `${window.location.origin}${redirectUrlPath}`
    : `${window.location.origin}/settings/connection-sp`;
  const authUrl =
    `${domain}/apps/authorize/consent` +
    `?application_id=${applicationId}&state=${state}&version=beta` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}`;
  window.location.href = authUrl;
}

export const session = {
  user,
  isAuthLoading,
  isInitializing,
  isAuthenticated,
  isSocialLogin,
  hasSession,
  isSessionExpired,
  getSessionToken,
  getSessionBpId,
  getSessionAccessToken,
  getSessionScopes,
  getValidSessionToken,
  refreshSession,
  switchToDefaultProfile,
  claimDefaultProfile,
  clearSession,
  setSession,
  setFederatedSession,
  seedActiveProfile,
  restore,
  setRouter,
  login,
  verifyMfa,
  recoveryVerify,
  logout,
  onSignOut,
  register,
  confirmRegistration,
  resendSignUpCode,
  resetPassword,
  confirmPasswordReset,
  updatePassword,
  setPassword,
  updateProfile,
  refreshUser,
  removeInvite,
  identifyLogRocket,
  handleLoginWithAmazonAds,
  handleLoginWithAmazonSellingPartner,
};
